<template>
  <div class="header-city">
    <div
      class="header-city__select"
      v-clickoutside="close"
      :class="{ open: isOpen }"
    >
      <button
        aria-controls="header-city-panel"
        class="header-city__toggle"
        type="button"
        :aria-expanded="isOpen"
        @click="toggle"
      >
        <svg aria-hidden="true" class="icon icon--large">
          <use href="#location-dot" />
        </svg>

        <span v-if="selected" v-text="selected.label" />
        <span class="skeleton-loader-text" v-else-if="isLoading || cityTerm" />
        <span v-else v-text="texts.chooseCity" />
      </button>

      <div class="header-city__panel" id="header-city-panel" v-show="isOpen">
        <CityPanel
          ref="panel"
          :city-changed="internallySelected"
          :city-term="cityTerm"
          :closable="true"
          @close="close"
          @select="setSelectedTerm"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import CityPanel from "Components/CityPanel.vue";
  import { useCityTermLocalStorage } from "Main/composables/useCityTermLocalStorage";
  import { pushCitySelectOpen, pushNewCitySelected } from "Utils/city-tracking";
  import { mapState } from "vuex";

  /** Matches the dropdown's opening transition, so the input is visible when focused. */
  const FOCUS_DELAY = 350;

  export default {
    beforeUnmount() {
      window.removeEventListener(
        "city-storage-updated-event",
        this.eventUpdated
      );
    },

    components: {
      CityPanel,
    },

    computed: {
      ...mapState("cities", ["cityLinks", "isLoadingCityLinks"]),

      isLoading() {
        return this.isLoadingCityLinks;
      },

      selected() {
        if (!this.cityTerm) {
          return null;
        }

        return {
          choice: this.cityTerm.id,
          label: this.cityTerm.name,
          slug: this.cityTerm.slug,
        };
      },
    },

    data() {
      return {
        internallySelected: false,
        isOpen: false,
        texts: theme_vars.cityCaseTypeSelect,
      };
    },

    methods: {
      close() {
        this.isOpen = false;
      },
      eventUpdated() {
        this.cityTerm = this.getCityTermFromLocalStorage();
        this.getCityLinks();
      },

      getCityLinks() {
        if (!this.cityTerm) {
          return;
        }

        this.$store.dispatch("cities/getCityLink", this.cityTerm.id);
      },

      open() {
        this.isOpen = true;
        pushCitySelectOpen("header");

        setTimeout(() => this.$refs.panel?.focusSearch(), FOCUS_DELAY);
      },

      setSelectedTerm(value) {
        this.internallySelected = true;
        this.cityTerm = value
          ? { id: value.choice, name: value.label, slug: value.slug }
          : null;

        this.saveCityTermToLocalStorage();
        this.getCityLinks();
      },

      toggle() {
        if (this.isOpen) {
          this.close();
          return;
        }

        this.open();
      },
    },

    mounted() {
      if (this.cityTerm) {
        this.getCityLinks();
      }

      window.addEventListener("city-storage-updated-event", this.eventUpdated);
    },
    name: "HeaderCity",

    setup() {
      const {
        cityTerm,
        saveCityTermToLocalStorage,
        getCityTermFromLocalStorage,
      } = useCityTermLocalStorage();
      return {
        cityTerm,
        getCityTermFromLocalStorage,
        saveCityTermToLocalStorage,
      };
    },

    watch: {
      // The mobile menu keeps its own copy of the city term, so a pick made there
      // only reaches this component through the shared store. Re-read on every
      // link refresh to stay in step with it.
      cityLinks() {
        this.cityTerm = this.getCityTermFromLocalStorage();
      },
      selected(newValue, oldValue) {
        // newValue is null whenever the city is cleared — a pick reset, or a
        // re-read of local storage that no longer holds one.
        if (
          !(this.internallySelected && oldValue && newValue) ||
          newValue.label === oldValue.label
        ) {
          return;
        }

        pushNewCitySelected("header", newValue.label, oldValue.label);
      },
    },
  };
</script>
